// Callable functions with Firebase's shapes (firebase/functions → @estra/client/functions):
//
//   const functions = getFunctions({ url: "https://api.example.com", getToken: () => auth.getIdToken() });
//   const addMessage = httpsCallable<{ text: string }, { ok: boolean }>(functions, "addMessage");
//   const { data } = await addMessage({ text: "hi" });
//
// Values travel as JSON (as with Firebase callables; Timestamps arrive as plain objects).

export interface Functions {
  url: string;
  getToken?: () => Promise<string | null> | string | null;
  fetch?: typeof fetch;
}

export class FunctionsError extends Error {
  /** "functions/<code>" as in the Firebase SDK: functions/not-found, functions/permission-denied … */
  readonly code: string;
  readonly details: unknown;
  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.code = `functions/${code}`;
    this.details = details;
    this.name = "FunctionsError";
  }
}

export const getFunctions = (o: Functions): Functions => o;

export interface HttpsCallableOptions {
  /** Milliseconds before the call fails with functions/deadline-exceeded (default 70 s, like Firebase). */
  timeout?: number;
}
export type HttpsCallable<Req, Res> = (data?: Req | null) => Promise<{ data: Res }>;

export function httpsCallable<Req = unknown, Res = unknown>(f: Functions, name: string, o: HttpsCallableOptions = {}): HttpsCallable<Req, Res> {
  return async (data) => {
    const token = f.getToken ? await f.getToken() : null;
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (token) headers.authorization = `Bearer ${token}`;
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), o.timeout ?? 70_000);
    let r: Response;
    try {
      r = await (f.fetch ?? fetch)(`${f.url.replace(/\/$/, "")}/v1/fn/${encodeURIComponent(name)}`, { method: "POST", headers, body: JSON.stringify({ data: data ?? null }), signal: ac.signal });
    } catch {
      throw ac.signal.aborted ? new FunctionsError("deadline-exceeded", "the call timed out") : new FunctionsError("unavailable", "network request failed");
    } finally {
      clearTimeout(timer);
    }
    const j = (await r.json().catch(() => ({}))) as { result?: Res; error?: { code: string; message: string; details?: unknown } };
    if (!r.ok || j.error) throw new FunctionsError(j.error?.code ?? (r.status >= 500 ? "internal" : "unknown"), j.error?.message ?? `HTTP ${r.status}`, j.error?.details);
    return { data: j.result as Res };
  };
}
